import { useEffect, useMemo, useState } from "react";

import {
  PalAutoComplete,
  PalIconButton,
  PalLink,
  PalMenu,
  PalMenuItem,
  PalTextField,
  PalTooltip,
  PalTypography,
} from "@palamar/fe-library";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import styled from "@emotion/styled";
import { Add, CreditCard, Store, Widgets } from "@mui/icons-material";
import { Alert, Box, Chip, Fade, FormHelperText, Grid, ListItemIcon } from "@mui/material";

import { ReactComponent as ShopifyIcon } from "~assets/images/onboarding/shopify.svg";
import useQueryParams from "~common/hooks/useQueryParams";
import { projectLanguageOptions } from "~constants";
import StyledHelper from "~helpers/StyledHelper";
import { selectShopifyIntegrations } from "~store/settings/eCommerce/selectors";
import { getProjectTemplates } from "~store/settings/projectSettings/actions";
import {
  selectProject,
  selectProjectLanguages,
  selectProjectTemplates,
} from "~store/settings/projectSettings/selectors";

import { RequiredComp, StepContainer } from "../CommonComponents";

const TemplateCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  ${(props) => props.disabled && `pointer-events: none; opacity: ${props.theme.palette.action.disabledOpacity};`}
`;
const TemplateCardItem = styled("div", StyledHelper.sfw("selected"))`
  user-select: none;
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.spacing(0.5)};
  border: 1px solid #e0e0e0;
  padding: 16px;

  align-items: start;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #50c8f4;
  }
  height: 140px;
  ${(props) =>
    props.selected &&
    `
    border-color: #50c8f4;
    background-color: #f5f5f5; 
    box-shadow: 0 0 5px 0 #50c8f4; 
  `}

  .name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .logo {
    display: flex;
    justify-content: center;
    svg {
      font-size: 24px;
    }
  }

  ${(props) => props.disabled && `pointer-events: none; opacity: ${props.theme.palette.action.disabledOpacity};`}
`;

const TemplateAddNewContainer = styled("div")`
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 140px;
  width: 140px;
`;

const shopifyTemplatePrefix = "shopify_";
function StepBasic({ formik, setFieldValue, stepKey }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // const location = useLocation();

  const { shopifyCode, shopifyInt, shopifyConnDone, shopifyConnId } = useQueryParams();

  const projectTemplates = useSelector(selectProjectTemplates);
  const projectLanguages = useSelector(selectProjectLanguages);
  const project = useSelector(selectProject);
  const shopifyIntegrations = useSelector(selectShopifyIntegrations);

  // const [selectedTemplate, setSelectedTemplate] = useState(null);
  // const [selectedShopifyIntegration, setSelectedShopifyIntegration] = useState(null);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);

  const isShopifyOnboarding = shopifyInt === "true";

  const allVisibleTemplates = useMemo(() => {
    const result = [];

    let templates = projectTemplates.filter((item) => {
      if (item.key.startsWith(shopifyTemplatePrefix)) {
        return isShopifyOnboarding ? true : false;
      }
      return true;
    });
    templates = templates.map((item) => ({
      ...item,
      isTemplate: true,
    }));
    // if (!isShopifyOnboarding) {
    result.push(...templates);
    // }
    result.push(...shopifyIntegrations);
    return result;
  }, [projectTemplates, shopifyIntegrations, isShopifyOnboarding]);

  const selectedTemplateItem = useMemo(() => {
    return allVisibleTemplates[selectedTemplateIndex] || null;
  }, [allVisibleTemplates, selectedTemplateIndex]);

  const translatedProjectLanguageOptions = useMemo(() => {
    let langs = projectLanguageOptions.map((item) => ({ ...item, label: t(item.label) }));
    if (selectedTemplateItem) {
      if (selectedTemplateItem.isTemplate) {
        langs = langs.filter((item) => selectedTemplateItem.bot_languages.includes(item.value));
      } else if (projectTemplates?.length > 0) {
        // const firstShopifyTemplate = projectTemplates[0];
        const firstShopifyTemplate = projectTemplates.find((item) => item.key.startsWith(shopifyTemplatePrefix));
        const shopifyLangs = firstShopifyTemplate.bot_languages;
        if (shopifyLangs?.length > 0) {
          langs = langs.filter((item) => shopifyLangs.includes(item.value));
        }
      }
    }
    return langs;
  }, [t, projectTemplates, selectedTemplateItem]);

  const handleChangeLanguage = (e, value) => {
    setFieldValue("assistant_languages", value || null);
  };

  useEffect(() => {
    if (!project || !projectTemplates) return;
    const fromTemplateId = project?.from_template;
    if (fromTemplateId) {
      const targetTemplateItemIndex = allVisibleTemplates.findIndex((item) => item.id === fromTemplateId);
      // setSelectedTemplate(projectTemplates.find((item) => item.id === fromTemplateId));
      if (targetTemplateItemIndex >= 0) {
        setSelectedTemplateIndex(targetTemplateItemIndex);
      } else {
        setSelectedTemplateIndex(null);
      }
    } else if (isShopifyOnboarding) {
      const firstShopifyTemplate = projectTemplates.find((item) => item.key.startsWith(shopifyTemplatePrefix));
      if (firstShopifyTemplate) {
        const targetTemplateItemIndex = allVisibleTemplates.findIndex((item) => item.id === firstShopifyTemplate.id);
        setSelectedTemplateIndex(targetTemplateItemIndex);
      } else {
        setSelectedTemplateIndex(-1);
      }
    }
  }, [project, projectTemplates, allVisibleTemplates, isShopifyOnboarding]);

  useEffect(() => {
    if (formik.touched?.[stepKey]?.assistant_languages) {
      formik.setTouched({ ...formik.touched, [stepKey]: { ...formik.touched[stepKey], assistant_template: true } });
    }
  }, [formik.touched?.[stepKey]?.assistant_languages, stepKey]);

  useEffect(() => {
    if (selectedTemplateItem) {
      if (selectedTemplateItem.isTemplate) {
        setFieldValue("assistant_template", selectedTemplateItem?.id);
        setFieldValue("assistant_template_type", null);
      } else {
        setFieldValue("assistant_template", selectedTemplateItem?.id || null);
        setFieldValue("assistant_template_type", "shopify");
      }
    }
  }, [selectedTemplateItem, projectTemplates]);

  useEffect(() => {
    dispatch(getProjectTemplates({}));
  }, [dispatch]);

  const handleClickSelectTemplateItem = (templateItemIndex) => () => {
    setSelectedTemplateIndex(templateItemIndex);
  };

  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            Start with the Basics
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please fill out the following information to get started.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Project Name
            <RequiredComp />
          </PalTypography>
          <PalTextField
            fullWidth
            invalid={formik.touched[stepKey]?.assistant_name && formik.errors[stepKey]?.assistant_name}
            name={`${stepKey}.assistant_name`}
            placeholder="Enter Project Name"
            value={formik.values[stepKey].assistant_name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} variant="body1">
            Primary Language
            <RequiredComp />
          </PalTypography>
          <PalAutoComplete
            fullWidth
            isPrimitiveValue
            invalid={formik.touched[stepKey]?.assistant_languages && formik.errors[stepKey]?.assistant_languages}
            name={`${stepKey}.assistant_languages`}
            options={translatedProjectLanguageOptions}
            placeholder="Enter Primary Language"
            value={formik.values[stepKey].assistant_languages}
            onBlur={formik.handleBlur}
            onChange={handleChangeLanguage}
          />
        </Grid>
        {/* <Grid item xs={12}>
          <PalTypography gutterBottom fontWeight={600} m="0" variant="body1">
            <PalSwitch
              checked={showTemplate}
              disabled={!!project?.from_template}
              onChange={handleClickToggleTemplateShow}
            />
            Create Assistant from Template
          </PalTypography>
        </Grid> */}
        {!isShopifyOnboarding && !project?.shopify_integration && (
          <Fade mountOnEnter unmountOnExit in={selectedTemplateIndex > -1 || project?.id}>
            <Grid item xs={12}>
              <Alert severity="info">Selected template cannot be changed after the assistant is created.</Alert>
            </Grid>
          </Fade>
        )}
        {!project?.shopify_integration && !isShopifyOnboarding && (
          <Grid item xs={12}>
            <TemplateCardContainer disabled={!!project?.from_template || project?.id}>
              {allVisibleTemplates.map((item, index) => {
                const handleClickGoToDeepLink = (e) => {
                  e.stopPropagation();
                  if (!item.isTemplate) {
                    window.open(`https://${item.deep_link}`, "_blank");
                  }
                };
                const handleClickGoToPricing = (e) => {
                  e.stopPropagation();
                  if (!item.isTemplate) {
                    window.open(`https://${item.plan_page_link}`, "_blank");
                  }
                };
                return (
                  <TemplateCardItem
                    key={index}
                    disabled={!item.isTemplate && item.project}
                    selected={selectedTemplateIndex === index}
                    onClick={handleClickSelectTemplateItem(index)}
                  >
                    <Box alignItems="start" display="flex" gap={1}>
                      <div className="logo">
                        <Widgets />
                      </div>
                      <PalTypography gutterBottom className="name" fontWeight={600} variant="body1">
                        {item.isTemplate ? item.name : item.metadata?.name}
                      </PalTypography>
                    </Box>

                    {!item.isTemplate && (
                      <Box alignItems="start" display="flex" gap={1} overflow="hidden" width={1}>
                        <PalTypography gutterBottom noWrap variant="caption">
                          <PalLink href={`https://${item.metadata?.domain}`} rel="noopener noreferrer" target="_blank">
                            {`https://${item.metadata?.domain}`}
                          </PalLink>
                        </PalTypography>
                      </Box>
                    )}

                    <Box mt="auto" width={1}>
                      {item.isTemplate && (
                        <PalTypography gutterBottom variant="caption">
                          Supported Languages
                        </PalTypography>
                      )}
                      <br />
                      {item.isTemplate ? (
                        <>
                          {item.bot_languages.map((lang) => (
                            <Chip key={lang} label={lang} size="small" />
                          ))}
                        </>
                      ) : (
                        <>
                          <Box alignItems="center" display={"flex"} gap={1} justifyContent={"space-between"} width={1}>
                            <Box
                              sx={{
                                width: 72,
                              }}
                            >
                              <ShopifyIcon />
                            </Box>
                            <Box alignItems="center" display={"flex"} gap={0.5}>
                              <PalTooltip title="Go to Storefront">
                                <PalIconButton size="small" onClick={handleClickGoToDeepLink}>
                                  <Store />
                                </PalIconButton>
                              </PalTooltip>
                              <PalTooltip title={"Go to Plan Page"}>
                                <PalIconButton size="small" onClick={handleClickGoToPricing}>
                                  <CreditCard />
                                </PalIconButton>
                              </PalTooltip>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  </TemplateCardItem>
                );
              })}
              <AddNewIntegrationCard />
            </TemplateCardContainer>
            {formik.touched[stepKey]?.assistant_template && formik.errors[stepKey]?.assistant_template && (
              <FormHelperText error>{formik.errors[stepKey]?.assistant_template}</FormHelperText>
            )}
          </Grid>
        )}
      </Grid>
    </StepContainer>
  );
}

function AddNewIntegrationCard() {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShopifyClick = () => {
    handleMenuClose();
    window.open("https://apps.shopify.com/palmate", "_blank");
  };

  return (
    <TemplateAddNewContainer>
      <PalIconButton size="large" onClick={handleButtonClick}>
        <Add fontSize="large" />
      </PalIconButton>
      <PalMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <PalMenuItem onClick={handleShopifyClick}>
          <ListItemIcon>
            <Store fontSize="small" />
          </ListItemIcon>
          <PalTypography variant="inherit">Add Shopify Integration</PalTypography>
        </PalMenuItem>
      </PalMenu>
    </TemplateAddNewContainer>
  );
}

export default StepBasic;
